import { trackError } from '@cint/analytics';
import { useToast } from '@cint/wonderment';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';

import { generateErrorNotifications } from '@/utils/generateErrorNotifications';

import httpClient, { isMaintenanceModeError } from './httpClient';

interface ErrorsResponse {
  errors: {
    [key: string]: Array<{ message: string; code: string }>;
  };
}

interface UsePostDataParams {
  url: string;
  successMessage?: string | ((response: any) => string);
  errorMessage?: string | ((error: AxiosError) => string);
  method?: 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  showErrorToast?: boolean;
  includeDetailError?: boolean;
  etagSourceUrl?: string;
}

type ApiResponse<T> = {
  data: T;
  headers: Record<string, any>;
};

export const etagStore = new Map<string, string>();

const storeEtagFromHeaders = (
  url: string,
  headers: Record<string, any>
): void => {
  const etag = headers['etag'];
  if (etag) {
    etagStore.set(url, etag);
  }
};

const getEtag = (url: string, etagSourceUrl?: string): string | undefined =>
  etagStore.get(etagSourceUrl || url);

const fetchData = async <T>(
  url: string,
  params?: Record<string, any>,
  opts?: { signal?: AbortSignal }
): Promise<ApiResponse<T>> => {
  const { data, headers } = await httpClient.get(url, {
    params,
    signal: opts?.signal,
  });

  return { data, headers };
};

interface UseFetchDataParams {
  url: string;
  params?: Record<string, any>;
  enabled?: boolean;
  staleTime?: number;
  keepPreviousData?: boolean;
}

export const useFetchData = <T>({
  url,
  params,
  enabled = true,
  staleTime = 0,
  keepPreviousData: shouldKeepPreviousData = true,
}: UseFetchDataParams): UseQueryResult<T, AxiosError> => {
  const queryKey = params ? [url, JSON.stringify(params)] : [url];

  return useQuery<T, AxiosError>({
    queryKey,
    queryFn: async () => {
      const { data, headers } = await fetchData<T>(url, params);
      storeEtagFromHeaders(url, headers);

      return data;
    },
    enabled,
    staleTime,
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    refetchOnWindowFocus: false,
  });
};

export const usePostData = <T, TData = any>({
  url,
  successMessage,
  errorMessage,
  method = 'POST',
  headers: customHeaders = {},
  showErrorToast = true,
  includeDetailError = false,
  etagSourceUrl,
}: UsePostDataParams): UseMutationResult<
  AxiosResponse<T>,
  AxiosError,
  TData | undefined
> => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<T>, AxiosError, TData | undefined>({
    mutationFn: (newData?: TData) => {
      const etag = getEtag(url, etagSourceUrl);
      const headers = new AxiosHeaders(customHeaders);
      if (etag) {
        headers.set('If-Match', etag);
      }

      return httpClient({
        method,
        url,
        data: newData,
        headers,
      });
    },
    onSuccess: (response) => {
      const etagKey = etagSourceUrl || url;
      storeEtagFromHeaders(etagKey, response.headers);

      if (successMessage) {
        toast({
          description:
            typeof successMessage === 'function'
              ? (successMessage as (response: any) => string)(response)
              : successMessage,
          variant: 'success',
        });
      }
      queryClient.invalidateQueries({ queryKey: [url] });
      if (etagSourceUrl) {
        queryClient.invalidateQueries({ queryKey: [etagSourceUrl] });
      }
    },
    onError: (error) => {
      // Maintenance mode redirects to its own page; a toast on the way out
      // would be noise.
      if (isMaintenanceModeError(error)) {
        return;
      }

      const errorMsg =
        typeof errorMessage === 'function'
          ? (errorMessage as (error: AxiosError) => string)(error)
          : errorMessage;

      trackError(errorMsg || error.message);
      if (!error.response) {
        toast({
          description: 'Network error, please try again later.',
          variant: 'error',
        });
      } else if (error.response && showErrorToast) {
        const errorNotifications = generateErrorNotifications({
          errorMessage: error.response.data as ErrorsResponse,
          defaultErrorMessage: errorMsg,
          statusCode: error.response.status,
          includeCodeInErrorMessage: includeDetailError,
        });

        errorNotifications.forEach((errorNotification) =>
          toast({
            customDescription: errorNotification,
            variant: 'error',
          })
        );
      }
    },
  });
};

interface UseFetchManyDataParams {
  urls: Array<string | undefined>;
  enabled?: boolean;
  staleTime?: number;
}

export const useFetchManyData = <T>({
  urls,
  enabled = true,
  staleTime = 0,
}: UseFetchManyDataParams): UseQueryResult<T, AxiosError>[] => {
  return useQueries({
    queries: urls.map((url) => ({
      queryKey: [url],
      enabled: enabled && !!url,
      staleTime,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        if (!url) throw new Error('Missing URL');
        const { data, headers } = await fetchData<T>(url);
        storeEtagFromHeaders(url, headers);
        return data;
      },
    })),
  }) as UseQueryResult<T, AxiosError>[];
};

interface UseDownloadFileParams {
  url: string;
  params?: Record<string, any>;
  enabled?: boolean;
  staleTime?: number;
}

const downloadFile = async (
  url: string,
  params?: Record<string, any>
): Promise<ApiResponse<Blob>> => {
  const response = await httpClient.get<Blob>(url, {
    params,
    responseType: 'blob',
  });
  return { data: response.data, headers: response.headers };
};

export const useDownloadFile = ({
  url,
  params,
  enabled = true,
  staleTime = 0,
}: UseDownloadFileParams): UseQueryResult<ApiResponse<Blob>, AxiosError> => {
  return useQuery<ApiResponse<Blob>, AxiosError>({
    queryKey: [url, params],
    queryFn: async () => {
      const result = await downloadFile(url, params);
      storeEtagFromHeaders(url, result.headers);
      return result;
    },
    enabled,
    staleTime,
  });
};

interface UseInfiniteQueryParams {
  url: string;
  params?: Record<string, any>;
  pageSize?: number;
  enabled?: boolean;
  staleTime?: number;
  ignoreStartAfterId?: boolean;
}

export const useInfiniteQueryData = <
  T extends { has_more?: boolean; start_after_id?: string },
>({
  url,
  params,
  pageSize = 10,
  enabled = true,
  staleTime = 0,
  ignoreStartAfterId = false,
}: UseInfiniteQueryParams) => {
  return useInfiniteQuery<T, AxiosError, any, any, string | number | null>({
    queryKey: [url, params],
    queryFn: async ({ pageParam }) => {
      const queryParams = ignoreStartAfterId
        ? {
            ...params,
            page_size: pageSize,
            page_number: pageParam ?? 1,
          }
        : {
            ...params,
            page_size: pageSize,
            ...(pageParam && { start_after: pageParam }),
          };

      const { data, headers } = await fetchData<T>(url, queryParams);
      storeEtagFromHeaders(url, headers);
      return data;
    },
    initialPageParam: ignoreStartAfterId ? 1 : null,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_more
        ? ignoreStartAfterId
          ? allPages.length + 1
          : lastPage.start_after_id
        : null,
    enabled,
    staleTime,
  });
};
